import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  Alert,
  Autocomplete,
  Box,
  Typography
} from '@mui/material';
import { collection, onSnapshot, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from './firebase';
import { useAuth } from './AuthContext';
import { ACTIVITY_TYPES, ACTIVITY_LABELS, OUTCOMES } from './constants';
import { nowISO } from './helpers';

function localDateTimeValue(value = new Date()) {
  const d = value instanceof Date ? new Date(value) : new Date(value);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function formatContactLabel(c) {
  const nome = (c?.nome || '').trim();
  const azienda = (c?.azienda || '').trim();

  if (azienda && nome && azienda !== nome) {
    return `${azienda} — ${nome}`;
  }

  return azienda || nome || 'Contatto senza nome';
}

export default function ActivityModal({ initial, onClose }) {
  const { staff } = useAuth();
  const [contatti, setContatti] = useState([]);
  const [saving, setSaving] = useState(false);
  const [contactQuery, setContactQuery] = useState('');

  const [f, setF] = useState({
    tipo: initial?.tipo || 'call',
    data: initial?.data ? initial.data.slice(0, 16) : localDateTimeValue(),
    owner: initial?.owner || staff?.nome || 'Mario',
    contattoId: initial?.contattoId || '',
    esito: initial?.esito || 'Da richiamare',
    note: initial?.note || '',
    prossimoStep: initial?.prossimoStep || '',
    id: initial?.id || null
  });

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'crm_contatti'), s => {
      setContatti(s.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  const up = (k, v) => {
    setF(prev => ({ ...prev, [k]: v }));
  };

  const isFuture = useMemo(() => {
    if (!f.data) return false;
    return new Date(f.data) > new Date();
  }, [f.data]);

  const selectedContact = useMemo(
    () => contatti.find(c => c.id === f.contattoId) || null,
    [contatti, f.contattoId]
  );

  useEffect(() => {
    if (selectedContact) {
      setContactQuery(formatContactLabel(selectedContact));
    }
  }, [selectedContact]);

  const filteredContatti = useMemo(() => {
    const q = contactQuery.trim().toLowerCase();

    if (q.length < 2) return [];

    return contatti
      .filter(c => {
        const haystack = [
          c.nome || '',
          c.azienda || '',
          c.telefono || '',
          c.email || '',
          c.citta || ''
        ]
          .join(' ')
          .toLowerCase();

        return haystack.includes(q);
      })
      .sort((a, b) => formatContactLabel(a).localeCompare(formatContactLabel(b)))
      .slice(0, 12);
  }, [contatti, contactQuery]);

  const submit = async () => {
    if (!f.tipo || !f.data) return;

    setSaving(true);
    try {
      // FIX: in creazione owner = chi è loggato.
      // In modifica preserva l'owner originale (non sovrascrive).
      const finalOwner = f.id
        ? f.owner  // modifica: mantieni owner originale
        : (staff?.nome || 'Mario');  // creazione: owner = chi crea

      const payload = {
        ...f,
        owner: finalOwner,
        contattoId: f.contattoId || null,
        data: f.data.length === 16 ? `${f.data}:00` : f.data,
        esito: isFuture ? 'Pianificata' : f.esito
      };

      if (f.id) {
        const { id, ...rest } = payload;
        await updateDoc(doc(db, 'crm_attivita', id), rest);
      } else {
        const { id, ...rest } = payload;
        await addDoc(collection(db, 'crm_attivita'), {
          ...rest,
          createdAt: nowISO()
        });
      }

      onClose();
    } catch (err) {
      console.error('Errore salvataggio attività:', err);
      alert('Errore durante il salvataggio dell\u2019attivit\u00e0.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {f.id ? 'Modifica attività' : 'Registra attività'}
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

          {isFuture && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Attività pianificata per il futuro — l'esito verrà registrato dopo lo svolgimento.
            </Alert>
          )}

          <Box sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: isFuture ? "repeat(3, 1fr)" : "repeat(4, 1fr)" },
            gap: 2
          }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Tipo"
              value={f.tipo}
              onChange={e => up('tipo', e.target.value)}
            >
              {ACTIVITY_TYPES.map(t => (
                <MenuItem key={t} value={t}>
                  {ACTIVITY_LABELS[t]}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              size="small"
              type="datetime-local"
              label="Data/Ora"
              InputLabelProps={{ shrink: true }}
              value={f.data}
              onChange={e => up('data', e.target.value)}
            />

            {!isFuture && (
              <TextField
                select
                fullWidth
                size="small"
                label="Esito"
                value={f.esito}
                onChange={e => up('esito', e.target.value)}
              >
                {OUTCOMES.map(o => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              fullWidth
              size="small"
              label="Owner"
              value={f.owner}
              InputProps={{ readOnly: true }}
            />
          </Box>

          <Autocomplete
            fullWidth
            size="small"
            disablePortal
            options={filteredContatti}
            value={selectedContact}
            inputValue={contactQuery}
            onInputChange={(_, value, reason) => {
              setContactQuery(value);
              if (reason === 'clear') up('contattoId', '');
            }}
            onChange={(_, value) => {
              up('contattoId', value?.id || '');
              setContactQuery(value ? formatContactLabel(value) : '');
            }}
            getOptionLabel={(option) => formatContactLabel(option)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={x => x}
            noOptionsText={contactQuery.trim().length < 2 ? 'Digita 2 lettere' : 'Nessun risultato'}
            ListboxProps={{ style: { maxHeight: 280 } }}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id} sx={{ py: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {formatContactLabel(option)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {[option.telefono, option.citta].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Contatto"
                placeholder="Cerca azienda, nome o telefono..."
              />
            )}
          />

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "7fr 5fr" }, gap: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              size="small"
              label="Note"
              value={f.note}
              onChange={e => up('note', e.target.value)}
            />

            <TextField
              fullWidth
              multiline
              rows={4}
              size="small"
              label="Prossimo step"
              value={f.prossimoStep}
              onChange={e => up('prossimoStep', e.target.value)}
            />
          </Box>

        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={saving}>
          Annulla
        </Button>
        <Button variant="contained" onClick={submit} disabled={saving}>
          {saving ? 'Salvataggio...' : 'Salva'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}